// ✅ src/pages/TarMixing/Tar_BOM_Entry.jsx (FULL FRONTEND - UPDATED WITH localStorage HEADERS)

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProductionLayoutWrapper from "../../components/ProductionLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import { FaArrowLeft } from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";
import {
  FaPlus,
  FaSave,
  FaTrash,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

const BASE_URL = "http://nissi-india.com";

// yyyy-MM-dd -> dd/MM/yyyy
const ddmmyyyyFromDateInput = (yyyyMMdd) => {
  if (!yyyyMMdd) return "";
  const [y, m, d] = String(yyyyMMdd).split("-");
  if (!y || !m || !d) return "";
  return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${y}`;
};

// dd/MM/yyyy -> yyyy-MM-dd
const toDateInputValue = (maybeDdMmYyyy) => {
  if (!maybeDdMmYyyy) return "";
  const s = String(maybeDdMmYyyy).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;

  const dt = new Date(s);
  if (isNaN(dt.getTime())) return "";
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const toNum = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};

const toInt = (v, def = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : def;
};

const Tar_BOM_Entry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const location = useLocation();

  // ✅ safe localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");

  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS =
    getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ parsed values
  const branchid = useMemo(() => toInt(branchIdLS, 0), [branchIdLS]);
  const branchtype = useMemo(
    () => String(branchTypeLS || "").trim(),
    [branchTypeLS]
  );
  const userid = useMemo(() => toInt(userIdLS, 0), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchid,
      "x-branch": branchtype,
      "x-branchtype": branchtype,
      "x-userid": userid,
    }),
    [finYearId, finYear, branchid, branchtype, userid]
  );

  const editMode = Boolean(location?.state?.editMode);
  const editRow = location?.state?.rowData || null;
  const editBomId = Number(location?.state?.bomId || editRow?.bomId || 0);

  const [formData, setFormData] = useState({
    bomid: 0,
    entryDate: "",
    productionCode: "",
    productionQty: "",
    productionName: "",
    projectid: 0,
    estimationdetid: 0,
    uom: "",
  });

  const [derivedWorktypeId, setDerivedWorktypeId] = useState(null);
  const [projects, setProjects] = useState([]);
  const [workSubTypes, setWorkSubTypes] = useState([]);
  const [addItems, setAddItems] = useState([]);
  const [saveItems, setSaveItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [showAddItems, setShowAddItems] = useState(true);

  const setField = (field, value) =>
    setFormData((p) => ({ ...p, [field]: value }));

  // ✅ axios instance with headers
  const api = useMemo(
    () =>
      axios.create({
        baseURL: `${BASE_URL}/api/bomEntry`,
        headers: commonHeaders,
      }),
    [commonHeaders]
  );

  const loadProjects = async () => {
    const res = await api.get("/projects", {
      params: { branchid, userid, branchtype },
    });
    return res.data?.data || [];
  };

  const loadWorkSubTypes = async (projectid) => {
    const res = await api.get("/worksubtypes", {
      params: { branchid, userid, projectid },
    });
    return res.data?.data || [];
  };

  const generateCode = async () => {
    const res = await api.get("/generateCode", {
      params: { branchid, userid },
    });
    return res.data?.data?.productionCode || "";
  };

  const loadProductionValues = async ({ projectid, estimationdetid }) => {
    const res = await api.get("/productionValues", {
      params: { branchid, userid, projectid, estimationdetid },
    });
    return res.data?.data || null;
  };

  const loadItems = async ({ projectid }) => {
    const res = await api.get("/items", {
      params: { branchid, userid, projectid },
    });
    return res.data?.data || [];
  };

  const loadEdit = async (bomid) => {
    try {
      const res = await api.get("/edit", {
        params: { bomid },
      });
      return res.data?.data || null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        if (!branchid) {
          toast.error("branchId not found in localStorage");
          return;
        }

        const pr = await loadProjects();
        setProjects(pr);

        if (editMode && editBomId) {
          const editData = await loadEdit(editBomId);

          if (editData?.header) {
            const h = editData.header;

            setFormData((p) => ({
              ...p,
              bomid: editBomId,
              entryDate: toDateInputValue(h.entryDate),
              productionCode: String(h.productionCode || ""),
              productionQty: String(h.productionQty ?? ""),
              productionName: String(h.productionName || ""),
              projectid: Number(h.projectid || 0),
              estimationdetid: Number(h.estimationdetid || 0),
              uom: String(h.uom || ""),
            }));

            if (h.projectid) {
              const ws = await loadWorkSubTypes(h.projectid);
              setWorkSubTypes(ws);
            }

            if (h.projectid) {
              setLoadingItems(true);
              const items = await loadItems({ projectid: h.projectid });
              setAddItems(
                (items || []).map((x, idx) => ({
                  ...x,
                  rowKey: x.itemId || x.itemCode || idx,
                  selected: false,
                  reqQtyInput: "0",
                }))
              );
              setLoadingItems(false);
            }

            if (Array.isArray(editData.items)) {
              setSaveItems(
                editData.items.map((x, idx) => ({
                  rowKey: x.itemId || x.itemCode || idx,
                  itemId: x.itemId,
                  itemCode: x.itemCode,
                  itemName: x.itemName,
                  qty: toNum(x.qty, 0),
                  usedQty: toNum(x.usedQty, 0),
                  reqQty: toNum(x.reqQty, 0),
                }))
              );
            }
          } else if (editRow) {
            setFormData((p) => ({
              ...p,
              bomid: editBomId,
              entryDate: toDateInputValue(editRow.entryDate),
              productionCode: String(editRow.productionCode || ""),
              productionQty: String(editRow.productionQty ?? ""),
              productionName: String(editRow.productionName || ""),
              projectid: Number(editRow.projectId || 0),
              estimationdetid: Number(editRow.estimationdetid || 0),
              uom: String(editRow.tarUOM || ""),
            }));

            if (editRow.projectId) {
              const ws = await loadWorkSubTypes(editRow.projectId);
              setWorkSubTypes(ws);

              setLoadingItems(true);
              const items = await loadItems({ projectid: editRow.projectId });
              setAddItems(
                (items || []).map((x, idx) => ({
                  ...x,
                  rowKey: x.itemId || x.itemCode || idx,
                  selected: false,
                  reqQtyInput: "0",
                }))
              );
              setLoadingItems(false);
            }
          } else {
            toast.warn("Edit mode enabled but no bomId/rowData found");
          }
        } else {
          const code = await generateCode();
          setFormData((p) => ({
            ...p,
            bomid: 0,
            productionCode: String(code),
            entryDate: toDateInputValue(new Date().toISOString().slice(0, 10)),
            productionQty: "1",
          }));
        }
      } catch (err) {
        console.error("Init error:", err);
        toast.error(err?.response?.data?.message || "Failed to load page data");
      } finally {
        setLoading(false);
        setLoadingItems(false);
      }
    })();
  }, [branchid, editBomId, editMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const onProjectChange = async (projectid) => {
    const pid = Number(projectid || 0);

    setFormData((p) => ({
      ...p,
      projectid: pid,
      estimationdetid: 0,
      uom: "",
    }));

    setWorkSubTypes([]);
    setAddItems([]);
    setSaveItems([]);
    setDerivedWorktypeId(null);

    try {
      if (!pid) return;
      const ws = await loadWorkSubTypes(pid);
      setWorkSubTypes(ws);
    } catch (err) {
      console.error("WorkSubType load error:", err);
      toast.error(
        err?.response?.data?.message || "Failed to load work subtypes"
      );
    }
  };

  const onSubTypeChange = async (estimationdetid) => {
    const sid = Number(estimationdetid || 0);
    setField("estimationdetid", sid);

    setField("uom", "");
    setAddItems([]);
    setSaveItems([]);
    setDerivedWorktypeId(null);

    if (!formData.projectid) {
      toast.warn("Please select Project first");
      return;
    }
    if (!sid) return;

    try {
      setLoadingItems(true);

      const pv = await loadProductionValues({
        projectid: formData.projectid,
        estimationdetid: sid,
      });

      if (pv?.uom) setField("uom", String(pv.uom));
      if (pv?.productionQty !== undefined && pv?.productionQty !== null) {
        setField("productionQty", String(pv.productionQty));
      }
      if (pv?.worktypeid !== undefined && pv?.worktypeid !== null) {
        setDerivedWorktypeId(Number(pv.worktypeid));
      }

      const items = await loadItems({ projectid: formData.projectid });

      setAddItems(
        (items || []).map((x, idx) => ({
          ...x,
          rowKey: x.itemId || x.itemCode || idx,
          selected: false,
          reqQtyInput: String(x.reqQty ?? 0),
        }))
      );
    } catch (err) {
      console.error("SubType change error:", err);
      toast.error(
        err?.response?.data?.message ||
          "Failed to load production values/items"
      );
      setAddItems([]);
    } finally {
      setLoadingItems(false);
    }
  };

  const toggleAddItem = (rowKey) => {
    setAddItems((prev) =>
      prev.map((it) =>
        it.rowKey === rowKey ? { ...it, selected: !it.selected } : it
      )
    );
  };

  const setReqQty = (rowKey, value) => {
    setAddItems((prev) =>
      prev.map((it) =>
        it.rowKey === rowKey ? { ...it, reqQtyInput: value } : it
      )
    );
  };

  const addSelectedToSave = () => {
    const selected = addItems.filter((x) => x.selected);
    if (selected.length === 0) {
      toast.warn("Select at least one item to Add");
      return;
    }

    for (const it of selected) {
      const reqQty = toNum(it.reqQtyInput, 0);
      if (reqQty <= 0) {
        toast.warn(`Enter valid ReqQty for item: ${it.itemName || it.itemCode}`);
        return;
      }
      const qty = toNum(it.qty ?? it.productionQty, 0);
      const usedQty = toNum(it.usedQty, 0);
      if (qty - usedQty < reqQty) {
        toast.warn(
          `Only ${(qty - usedQty).toFixed(3)} available for ${
            it.itemName || it.itemCode
          }`
        );
        return;
      }
    }

    setSaveItems((prev) => {
      const map = new Map(prev.map((p) => [p.rowKey, p]));
      for (const it of selected) {
        map.set(it.rowKey, {
          rowKey: it.rowKey,
          itemId: it.itemId,
          itemCode: it.itemCode,
          itemName: it.itemName,
          qty: toNum(it.qty ?? it.productionQty, 0),
          usedQty: toNum(it.usedQty, 0),
          reqQty: toNum(it.reqQtyInput, 0),
        });
      }
      return Array.from(map.values());
    });

    setAddItems((prev) => prev.map((it) => ({ ...it, selected: false })));
    toast.success("Added to Save Items");
  };

  const removeSaveItem = (rowKey) => {
    setSaveItems((prev) => prev.filter((x) => x.rowKey !== rowKey));
  };

  const submit = async () => {
    if (!formData.entryDate) return toast.warn("Entry Date required");
    if (!formData.productionCode) return toast.warn("Production Code required");
    if (!formData.productionQty || Number(formData.productionQty) <= 0)
      return toast.warn("Production Qty required");
    if (!formData.productionName) return toast.warn("Production Name required");
    if (!formData.projectid) return toast.warn("Project required");
    if (!formData.estimationdetid) return toast.warn("Work Subtype required");
    if (!formData.uom) return toast.warn("UOM required");
    if (!saveItems.length) return toast.warn("Please Add items before submit");

    const payload = {
      bomid: formData.bomid || 0,
      branchid,
      userid,
      entryDate: ddmmyyyyFromDateInput(formData.entryDate),
      projectid: formData.projectid,
      estimationdetid: formData.estimationdetid,
      productionCode: String(formData.productionCode),
      productionName: String(formData.productionName),
      productionQty: toNum(formData.productionQty, 0),
      uom: String(formData.uom),
      items: saveItems.map((x) => ({
        itemId: x.itemId,
        itemCode: x.itemCode,
        itemName: x.itemName,
        qty: toNum(x.qty, 0),
        usedQty: toNum(x.usedQty, 0),
        reqQty: toNum(x.reqQty, 0),
      })),
      worktypeid: derivedWorktypeId,
    };

    try {
      setLoading(true);

      const res = await api.post("/save", payload, {
        headers: commonHeaders,
      });

      if (res.data?.success === false) {
        toast.error(res.data?.message || "Submit failed");
        return;
      }

      toast.success(res.data?.message || "Saved successfully!");
      navigate("/TarMixing/Tar_BOM_Listing");
    } catch (err) {
      console.error("Submit error:", err);
      toast.error(err?.response?.data?.message || "Submit failed");
    } finally {
      setLoading(false);
    }
  };

  const cardClass = `p-6 mt-6 rounded-2xl shadow-xl border ${
    isDark
      ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
      : "bg-white text-black"
  }`;

  return (
    <ProductionLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PRODUCTION BOM ENTRY</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />
          <button
      onClick={() => navigate("/TarMixing/Tar_BOM_Listing")}
     className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
            isDark
              ? "border-slate-600 text-slate-300 hover:bg-slate-800"
              : "border-slate-300 text-slate-700 hover:bg-slate-100"
          }`}
    >
      <FaArrowLeft/>Back
    </button>
        <div className={cardClass}>
          <div className="flex items-center justify-between mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              BILL OF MATERIAL
            </h1>
             <div className="flex items-center gap-3">
            <div className="text-sm opacity-80">
              {editMode ? "Edit Mode" : "New Entry"}
            </div>
          
    </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Entry Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.entryDate}
                  onChange={(e) => setField("entryDate", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Production Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.productionCode}
                  onChange={(e) => setField("productionCode", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                  readOnly
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Production Qty <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.productionQty}
                  onChange={(e) => setField("productionQty", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Production Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.productionName}
                  onChange={(e) => setField("productionName", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Project Name <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.projectid}
                  onChange={(e) => onProjectChange(e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value={0}>Select</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Work SubType Name <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.estimationdetid}
                  onChange={(e) => onSubTypeChange(e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value={0}>Select</option>
                  {workSubTypes.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  UOM <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.uom}
                  onChange={(e) => setField("uom", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>
            </div>
          </div>

          <div
            className={`p-4 rounded-lg border mb-6 ${
              isDark
                ? "bg-gray-800 border-gray-700"
                : "bg-blue-50 border-blue-200"
            }`}
          >
            <div className="flex justify-between items-center">
              <h2 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                ADD ITEMS
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowAddItems((s) => !s)}
                  className={`px-4 py-2 rounded-lg font-semibold shadow transition ${
                    isDark
                      ? "bg-gray-700 hover:bg-gray-600 text-white"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-800"
                  }`}
                >
                  {showAddItems ? (
                    <span className="inline-flex items-center gap-2">
                      <FaChevronUp /> Hide
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <FaChevronDown /> Show
                    </span>
                  )}
                </button>

                <button
                  onClick={addSelectedToSave}
                  disabled={loadingItems || addItems.length === 0}
                  className={`px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition-all duration-200 ${
                    loadingItems || addItems.length === 0
                      ? "opacity-60 cursor-not-allowed"
                      : ""
                  } ${buttonClass} ${hoverClass} text-white`}
                >
                  <span className="inline-flex items-center gap-2">
                    <FaPlus /> Add
                  </span>
                </button>
              </div>
            </div>

            {showAddItems && (
              <div className="mt-4">
                {loadingItems ? (
                  <div className={`p-8 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Loading items...
                  </div>
                ) : addItems.length === 0 ? (
                  <div
                    className={`p-8 text-center border-2 border-dashed ${
                      isDark
                        ? "border-gray-600 bg-gray-700/50 text-gray-300"
                        : "border-gray-300 bg-gray-50 text-gray-600"
                    }`}
                  >
                    Select Project + Work SubType to load items.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className={`min-w-full text-sm ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                      <thead className={`${isDark ? "bg-gray-700" : "bg-blue-100"}`}>
                        <tr>
                          <th className="px-3 py-2 text-left">Select</th>
                          <th className="px-3 py-2 text-left">S.No</th>
                          <th className="px-3 py-2 text-left">Item Code</th>
                          <th className="px-3 py-2 text-left">Item Name</th>
                          <th className="px-3 py-2 text-right">Qty</th>
                          <th className="px-3 py-2 text-right">Used Qty</th>
                          <th className="px-3 py-2 text-right">ReqQty</th>
                        </tr>
                      </thead>
                      <tbody className={`${isDark ? "bg-gray-800" : "bg-white"}`}>
                        {addItems.map((it, idx) => (
                          <tr
                            key={it.rowKey}
                            className={`border-t ${isDark ? "border-gray-700" : "border-gray-100"}`}
                          >
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={Boolean(it.selected)}
                                onChange={() => toggleAddItem(it.rowKey)}
                              />
                            </td>
                            <td className="px-3 py-2">{idx + 1}</td>
                            <td className="px-3 py-2">{it.itemCode}</td>
                            <td className="px-3 py-2">{it.itemName}</td>
                            <td className="px-3 py-2 text-right">
                              {toNum(it.qty ?? it.productionQty, 0).toFixed(3)}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {toNum(it.usedQty, 0).toFixed(3)}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <input
                                type="number"
                                value={it.reqQtyInput}
                                onChange={(e) =>
                                  setReqQty(it.rowKey, e.target.value)
                                }
                                className={`w-28 px-2 py-1 border rounded ${
                                  isDark
                                    ? "bg-gray-700 border-gray-600 text-white"
                                    : "bg-white border-gray-300 text-gray-900"
                                }`}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          <div
            className={`p-4 rounded-lg border mb-6 ${
              isDark
                ? "bg-gray-800 border-gray-700"
                : "bg-green-50 border-green-200"
            }`}
          >
            <div className="flex justify-between items-center">
              <h2 className={`text-lg font-semibold ${isDark ? "text-white" : "text-green-900"}`}>
                SAVE ITEMS
              </h2>

              <button
                onClick={submit}
                disabled={loading}
                className={`px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition-all duration-200 ${
                  loading ? "opacity-60 cursor-not-allowed" : ""
                } ${
                  isDark
                    ? "bg-green-600 hover:bg-green-700 text-white"
                    : "bg-green-600 hover:bg-green-700 text-white"
                }`}
              >
                <span className="inline-flex items-center gap-2">
                  <FaSave /> {loading ? "Submitting..." : "Submit"}
                </span>
              </button>
            </div>

            <div className="mt-4">
              {saveItems.length === 0 ? (
                <div
                  className={`p-8 text-center border-2 border-dashed ${
                    isDark
                      ? "border-gray-600 bg-gray-700/50 text-gray-300"
                      : "border-green-300 bg-green-50 text-green-700"
                  }`}
                >
                  Add items to save list, then click Submit.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className={`min-w-full text-sm ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                    <thead className={`${isDark ? "bg-gray-700" : "bg-green-100"}`}>
                      <tr>
                        <th className="px-3 py-2 text-left">S.No</th>
                        <th className="px-3 py-2 text-left">Item Code</th>
                        <th className="px-3 py-2 text-left">Item Name</th>
                        <th className="px-3 py-2 text-right">Qty</th>
                        <th className="px-3 py-2 text-right">Used Qty</th>
                        <th className="px-3 py-2 text-right">ReqQty</th>
                        <th className="px-3 py-2 text-center">Remove</th>
                      </tr>
                    </thead>
                    <tbody className={`${isDark ? "bg-gray-800" : "bg-white"}`}>
                      {saveItems.map((it, idx) => (
                        <tr
                          key={it.rowKey}
                          className={`border-t ${isDark ? "border-gray-700" : "border-gray-100"}`}
                        >
                          <td className="px-3 py-2">{idx + 1}</td>
                          <td className="px-3 py-2">{it.itemCode}</td>
                          <td className="px-3 py-2">{it.itemName}</td>
                          <td className="px-3 py-2 text-right">
                            {toNum(it.qty, 0).toFixed(3)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {toNum(it.usedQty, 0).toFixed(3)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {toNum(it.reqQty, 0).toFixed(3)}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <button
                              onClick={() => removeSaveItem(it.rowKey)}
                              className={`px-3 py-1 rounded font-semibold ${
                                isDark
                                  ? "bg-red-600 hover:bg-red-700 text-white"
                                  : "bg-red-600 hover:bg-red-700 text-white"
                              }`}
                              title="Remove"
                            >
                              <span className="inline-flex items-center gap-2">
                                <FaTrash /> Remove
                              </span>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProductionLayoutWrapper>
  );
};

export default Tar_BOM_Entry;